/**
 * @file utils/AuthContext.js
 * @description React context that holds the current Firebase user and a loading flag.
 * Supports guest mode — when user is null, components can still render
 * using localStorage for persistence.
 *
 * Usage:
 *   const { user, loading, isGuest } = useAuth();
 */

import { createContext, useContext, useEffect, useState } from "react";
import { onAuthChange } from "../services/firebase";

const GUEST_ID = "guest";

const AuthContext = createContext({ user: null, loading: true, isGuest: true });

/**
 * Provides Firebase auth state to all child components.
 * @param {{ children: React.ReactNode }} props
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthChange((u) => {
      setUser(u);
      setLoading(false);
    });
    return unsub;
  }, []);

  const isGuest = !user;
  const uid = user?.uid || GUEST_ID;

  return (
    <AuthContext.Provider value={{ user, loading, isGuest, uid }}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to consume the auth context.
 * @returns {{ user: import("firebase/auth").User|null, loading: boolean, isGuest: boolean, uid: string }}
 */
export function useAuth() {
  return useContext(AuthContext);
}
